/**
 * Authentication Context for Stenope Academy
 * 
 * Manages authentication state across the application.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import authService from '../services/authService';
import { ACADEMY_AUTH_TOKEN_KEY, ACADEMY_AUTH_USER_KEY } from '../utils/authStorage';
import { socketService } from '../services/socketService';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    // Initialize auth state from localStorage
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                const savedToken = localStorage.getItem(ACADEMY_AUTH_TOKEN_KEY);
                const savedUser = localStorage.getItem(ACADEMY_AUTH_USER_KEY);

                if (savedToken && savedUser) {
                    try {
                        // Verify token with server
                        const verifyRes = await authService.verifyToken(savedToken);

                        if (verifyRes && verifyRes.status === 'success') {
                            setToken(savedToken);
                            const parsedUser = JSON.parse(savedUser);
                            const updatedUser = {
                                ...parsedUser,
                                academyPathway: verifyRes.academyPathway
                            };
                            setUser(updatedUser);
                            localStorage.setItem(ACADEMY_AUTH_USER_KEY, JSON.stringify(updatedUser));
                            setIsAuthenticated(true);
                            authService.setAuthToken(savedToken);
                        } else {
                            // Token invalid, clean up
                            socketService.disconnect();
                            localStorage.removeItem(ACADEMY_AUTH_TOKEN_KEY);
                            localStorage.removeItem(ACADEMY_AUTH_USER_KEY);
                        }
                    } catch (error) {
                        // Network error - trust local token if it exists
                        console.warn('[AUTH] Could not verify token with server, using local token');
                        setToken(savedToken);
                        setUser(JSON.parse(savedUser));
                        setIsAuthenticated(true);
                        authService.setAuthToken(savedToken);
                    }
                }
            } catch (error) {
                console.error('[AUTH] Init error:', error);
                localStorage.removeItem(ACADEMY_AUTH_TOKEN_KEY);
                localStorage.removeItem(ACADEMY_AUTH_USER_KEY);
            } finally {
                setLoading(false);
            }
        };

        initializeAuth();
    }, []);

    const login = async (username, password) => {
        try {
            setLoading(true);
            let response = await authService.login(username, password);

            if (response.status === 'warning' && response.code === 'EXISTING_SESSION') {
                response = await authService.login(username, password, true);
            }

            if (response.status === 'success') {
                const { user: userData, token: userToken } = response;

                setUser(userData);
                setToken(userToken);
                setIsAuthenticated(true);

                localStorage.setItem(ACADEMY_AUTH_TOKEN_KEY, userToken);
                localStorage.setItem(ACADEMY_AUTH_USER_KEY, JSON.stringify(userData));
                authService.setAuthToken(userToken);

                return { success: true };
            } else {
                return { success: false, message: response.message };
            }
        } catch (error) {
            return {
                success: false,
                message: error.response?.data?.message || 'Login failed. Please try again.',
            };
        } finally {
            setLoading(false);
        }
    };

    const logout = async () => {
        socketService.disconnect();
        try {
            await authService.logout();
        } catch (error) {
            console.error('[AUTH] Logout error:', error);
        }

        setUser(null);
        setToken(null);
        setIsAuthenticated(false);
        
        // Clear ALL user-related localStorage data
        localStorage.removeItem(ACADEMY_AUTH_TOKEN_KEY);
        localStorage.removeItem(ACADEMY_AUTH_USER_KEY);
        localStorage.removeItem('progress');
        
        // Clear all quiz attempt data
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('quiz_')) {
                localStorage.removeItem(key);
            }
        });
        
        authService.clearAuthToken();
    };

    const setAuthData = (userToken, userData) => {
        setUser(userData);
        setToken(userToken);
        setIsAuthenticated(true);
        localStorage.setItem(ACADEMY_AUTH_TOKEN_KEY, userToken);
        localStorage.setItem(ACADEMY_AUTH_USER_KEY, JSON.stringify(userData));
        authService.setAuthToken(userToken);
    };

    const updateUserPathway = (pathway) => {
        if (!user) return;
        const updatedUser = {
            ...user,
            academyPathway: pathway
        };
        setUser(updatedUser);
        localStorage.setItem(ACADEMY_AUTH_USER_KEY, JSON.stringify(updatedUser));
    };

    const value = {
        user,
        token,
        isAuthenticated,
        loading,
        login,
        logout,
        setAuthData,
        updateUserPathway,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
